import * as React from 'react';

// NPM
import { animated } from 'react-spring';

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
	useTheme,
} from '@material-ui/core/styles';
import {
	Grid,
	GridProps,
	Paper,
	Typography,
	useMediaQuery,
	Avatar,
} from '@material-ui/core';

// Utility
import Utility from '../../domain/classes/Utility';

// Icons
// import CreditCardRoundedIcon from '@material-ui/icons/CreditCardRounded';
import GroupRoundedIcon from '@material-ui/icons/GroupRounded';
import DevicesRoundedIcon from '@material-ui/icons/DevicesRounded';
import TimerRoundedIcon from '@material-ui/icons/TimerRounded';

// Components
import { AnimatedAttributeContainer } from './AttributeContainer.Component';

// Enum
import JourneyStage from '../../domain/enums/JourneyStage';

// Interfaces
import IAppointmentType from '../../domain/interfaces/IAppointmentType';

// Classes
import Doctor from '../../domain/classes/Doctor';
import AppointmentBookingRequest from '../../domain/classes/AppointmentBookingRequest';

// Contexts
import { useApplicationManager } from '../../contexts/ApplicationManager.Context';
import { ROOT_ID } from '../../domain/utility/config';

export const styles = (theme: Theme) =>
	createStyles({
		root: {},
		paper: {
			cursor: 'pointer',
			padding: theme.spacing(2),
			[theme.breakpoints.down('xs')]: { padding: theme.spacing(1) },
		},
		container: { display: 'flex' },
		imgContainer: {
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			marginRight: theme.spacing(2),
			[theme.breakpoints.down('xs')]: {
				marginRight: theme.spacing(1),
			},
		},
		img: { width: theme.spacing(8), height: theme.spacing(8) },
	});

type ConditionalProps =
	| {
			useSelected?: false;
			apptType: IAppointmentType;
	  }
	| { useSelected: true; apptType?: IAppointmentType };

export interface AppointmentTypeItemProps extends GridProps {
	showImg?: boolean;
	clickable?: boolean;
}

const AppointmentTypeItem = React.forwardRef<
	HTMLDivElement,
	AppointmentTypeItemProps & WithStyles<typeof styles> & ConditionalProps
>(
	(
		{
			classes,
			className,
			showImg,
			clickable,
			apptType,
			useSelected,
			xs = 12,
			sm = 6,
			md = 4,
			...gridProps
		},
		ref
	) => {
		// Theme
		const theme = useTheme();
		const matchXs = useMediaQuery(theme.breakpoints.down('xs'));

		// Contexts
		const {
			handleJourneyStageChange,
			handleSelectedApptTypeChange,
			selectedApptType,
			handleApptTypeOverridesChange,
			appointmentBookingRequest,
			handleAppointmentBookingRequestChange,
		} = useApplicationManager();

		// Handlers
		const handleAppointmentTypeSelected = async () => {
			if (clickable && !useSelected && apptType) {
				const result = await Doctor.getProvidersOfAppointmentType(
					apptType.appointmentTypeId as number
				);

				handleApptTypeOverridesChange(result.data);

				handleSelectedApptTypeChange(apptType as IAppointmentType);
				handleAppointmentBookingRequestChange(
					AppointmentBookingRequest.parseAppointmentType(
						apptType.appointmentTypeId,
						appointmentBookingRequest
					)
				);

				handleJourneyStageChange(JourneyStage.PatientAttributesQuestionaire);

				Utility.scrollToTopOfElement(ROOT_ID);
			}
		};

		const showFaceToFaceIcon = (): boolean => {
			if (useSelected) {
				return (
					selectedApptType.deliveryMethod === 1 ||
					selectedApptType.deliveryMethod === 2
				);
			}

			return apptType?.deliveryMethod === 1 || apptType?.deliveryMethod === 2;
		};

		const showTelehealthIcon = (): boolean => {
			if (useSelected) {
				return (
					selectedApptType.deliveryMethod === 1 ||
					selectedApptType.deliveryMethod === 3
				);
			}

			return apptType?.deliveryMethod === 1 || apptType?.deliveryMethod === 3;
		};

		return (
			<Grid item xs={xs} sm={sm} md={md} ref={ref} {...gridProps}>
				<Paper
					className={classes.paper}
					square
					onClick={handleAppointmentTypeSelected}
					elevation={clickable ? 1 : 0}
					variant={clickable ? 'elevation' : 'outlined'}
				>
					<div className={classes.container}>
						{showImg && (
							<div className={classes.imgContainer}>
								<Avatar
									src={
										useSelected ? selectedApptType.photoUri : apptType?.photoUri
									}
									className={classes.img}
								/>
							</div>
						)}

						<Grid container spacing={matchXs ? 1 : 2}>
							<Grid item xs={12}>
								<Typography variant="body1" color="primary">
									<strong>
										{useSelected
											? selectedApptType.appointmentTypeName
											: apptType?.appointmentTypeName}
									</strong>
								</Typography>
							</Grid>

							<AnimatedAttributeContainer
								Icon={TimerRoundedIcon}
								minutes={
									useSelected
										? selectedApptType.minimumDuration
										: apptType?.minimumDuration
								}
							/>

							{/* <AnimatedAttributeContainer
								Icon={CreditCardRoundedIcon}
								money={
									useSelected
										? selectedApptType.preDepositAmount
										: apptType?.preDepositAmount
								}
							/> */}
							{showFaceToFaceIcon() && (
								<AnimatedAttributeContainer
									Icon={GroupRoundedIcon}
									text="Face-to-face"
								/>
							)}
							{showTelehealthIcon() && (
								<AnimatedAttributeContainer
									Icon={DevicesRoundedIcon}
									text="Telehealth"
								/>
							)}
						</Grid>
					</div>
				</Paper>
			</Grid>
		);
	}
);

const StyledAppointmentTypeItem = withStyles(styles, {
	classNamePrefix: Utility.generateClassName('AppointmentTypeItem'),
})(AppointmentTypeItem);

export default StyledAppointmentTypeItem;

export const AnimatedAppointmentTypeItem = animated(StyledAppointmentTypeItem);
